import { useEffect, useState, Fragment } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { Dialog, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import AuthForm from './AuthForm';
import Dashboard from './components/Welcome';
import GymPlanner from './components/GymPlanner';
import DietPlanner from './components/DietPlanner';
import WorkoutHistory from './components/WorkoutHistory';
import Profile from './components/Profile';
import AICoach from './components/AICoach';
import SubscriptionManager from './components/SubscriptionManager';
import BlogContainer from './components/BlogContainer';
import Settings from './components/Settings';
import ResetPassword from './ResetPassword';
import AnvilLogo from './assets/anvil.png';

// Import navigation icons
import DashboardIcon from './assets/Dashboard Default.png';
import GymPlannerIcon from './assets/Gym Planner Default.png';
import DietTrackerIcon from './assets/Diet Tracker Default.png';
import WorkoutHistoryIcon from './assets/Workout History Default.png';
import AICoachIcon from './assets/AI Coach Default.png';
import ProfileIcon from './assets/Profile & Stats Default.png';
import BlogIcon from './assets/Blog Default.png';
import SubscriptionIcon from './assets/Subscription Default.png';
import SettingsIcon from './assets/Settings Default.png';
import BullseyeIcon from './assets/Bullseye Default.png';





function MainApp() {
  


  const [user, setUser] = useState(null);
  const [userName, setUserName] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [menuOpen, setMenuOpen] = useState(false);

  // Easy to add new pages - just add them to this array
  const navigationItems = [
    { id: 'dashboard', name: 'Dashboard', icon: DashboardIcon, component: Dashboard },
    { id: 'gym', name: 'Gym Planner', icon: GymPlannerIcon, component: GymPlanner },
    { id: 'diet', name: 'Diet Tracker', icon: DietTrackerIcon, component: DietPlanner },
    { id: 'history', name: 'Workout History', icon: WorkoutHistoryIcon, component: WorkoutHistory },
    { id: 'coach', name: 'AI Coach', icon: AICoachIcon, component: AICoach },
    { id: 'profile', name: 'Profile & Stats', icon: ProfileIcon, component: Profile },
    { id: 'blog', name: 'Blog', icon: BlogIcon, component: BlogContainer },
    { id: 'subscription', name: 'Subscription', icon: SubscriptionIcon, component: SubscriptionManager },
    { id: 'settings', name: 'Settings', icon: SettingsIcon, component: Settings },
  ];

  useEffect(() => {
     const platform = Capacitor.getPlatform();

    if (platform === 'android') {
      // Put the webview below the status bar and color it
      StatusBar.setOverlaysWebView({ overlay: false });
      StatusBar.setBackgroundColor({ color: '#0f172a' });
      StatusBar.setStyle({ style: Style.Light });
      document.body.classList.add('android');
    } else if (platform === 'ios') {
      // iOS can keep overlaying + use safe areas
      StatusBar.setOverlaysWebView({ overlay: true });
      StatusBar.setStyle({ style: Style.Light });
      document.body.classList.add('ios');
    }
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
      
      // Load user's theme preference and name
      if (session?.user?.id) {
        try {
          const { data } = await supabase
            .from('user_profiles')
            .select('theme, name')
            .eq('user_id', session.user.id)
            .single();
          
          if (data?.theme) {
            document.documentElement.setAttribute('data-theme', data.theme);
          }
          if (data?.name) {
            setUserName(data.name);
          }
        } catch (err) {
          console.error('Error loading profile:', err);
        }
      }
    };
    getUser();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
    });

    // Listen for navigation events from Settings component
    const handleNavigateToSubscription = () => {
      setActiveTab('subscription');
    };
    window.addEventListener('navigate-to-subscription', handleNavigateToSubscription);

    // Listen for generic navigate-to events from Welcome and other components
    const handleNavigateTo = (event) => {
      const tabId = event.detail;
      // Check if tab exists in valid navigation IDs
      const validTabs = ['welcome', 'gym', 'diet', 'history', 'coach', 'profile', 'blog', 'subscription', 'settings'];
      if (validTabs.includes(tabId)) {
        setActiveTab(tabId);
      }
    };
    window.addEventListener('navigate-to', handleNavigateTo);

    // Listen for profile updates to refresh the user's name
    const handleProfileUpdate = async () => {
      if (user?.id) {
        try {
          const { data } = await supabase
            .from('user_profiles')
            .select('name')
            .eq('user_id', user.id)
            .single();
          
          if (data?.name) {
            setUserName(data.name);
          }
        } catch (err) {
          console.error('Error refreshing name:', err);
        }
      }
    };
    window.addEventListener('profile-updated', handleProfileUpdate);

    return () => {
      listener.subscription.unsubscribe();
      window.removeEventListener('navigate-to-subscription', handleNavigateToSubscription);
      window.removeEventListener('navigate-to', handleNavigateTo);
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const handleNavigation = (tabId) => {
    setActiveTab(tabId);
    setMenuOpen(false);
  };

  if (!user) return <AuthForm onLogin={setUser} />;

  const activeItem = navigationItems.find(item => item.id === activeTab);
  const ActiveComponent = activeItem?.component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-purple-500/20 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Menu Button and Logo */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setMenuOpen(true)}
                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all duration-200"
                aria-label="Open menu"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
              
              <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center">
                <img src={AnvilLogo} alt="Iron Path Logo" className="w-full h-full object-contain" />
              </div>
              
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-white">The Iron Path</h1>
                <p className="text-sm text-purple-300">Welcome back, {userName || user.email.split('@')[0]}</p>
              </div>
            </div>

            {/* Current Page Name (Mobile) */}
            <div className="sm:hidden">
              <span className="text-white font-semibold flex items-center space-x-2">
                <img 
                  src={activeItem?.icon} 
                  alt={activeItem?.name}
                  className="w-5 h-5 rounded object-cover"
                />
                <span className="text-sm">{activeItem?.name}</span>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar Menu */}
      <Transition.Root show={menuOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setMenuOpen}>
          {/* Background Overlay */}
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          {/* Sidebar Panel */}
          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="-translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="-translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-sm">
                    <div className="flex h-full flex-col bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 shadow-xl border-r border-purple-500/30">
                      {/* Menu Header */}
                      <div className="px-6 py-6 border-b border-purple-500/30">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center">
                              <img src={AnvilLogo} alt="Iron Path Logo" className="w-full h-full object-contain" />
                            </div>
                            <div>
                              <h2 className="text-lg font-bold text-white">The Iron Path</h2>
                              <p className="text-sm text-purple-300">Welcome back, {userName || user.email.split('@')[0]}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={handleLogout}
                              className="bg-red-500/80 hover:bg-red-500 text-white px-3 py-2 rounded-lg transition-all duration-200 backdrop-blur-sm border border-red-400/30 text-xs font-semibold"
                            >
                              Sign Out
                            </button>
                            <button
                              type="button"
                              className="rounded-lg p-2 text-purple-300 hover:bg-white/10 hover:text-white transition-colors"
                              onClick={() => setMenuOpen(false)}
                            >
                              <span className="sr-only">Close menu</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Navigation Items */}
                      <div className="flex-1 overflow-y-auto py-6 px-4">
                        <nav className="space-y-2">
                          {navigationItems.map((item) => (
                            <button
                              key={item.id}
                              onClick={() => handleNavigation(item.id)}
                              className={`w-full flex items-center space-x-4 px-4 py-4 rounded-lg transition-all duration-200 ${
                                activeTab === item.id
                                  ? 'bg-purple-600 text-white shadow-lg'
                                  : 'bg-white/5 text-purple-200 hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <img 
                                src={item.icon} 
                                alt={item.name}
                                className="w-8 h-8 rounded-md object-cover"
                              />
                              <span className="font-semibold text-base">{item.name}</span>
                              {activeTab === item.id && (
                                <span className="ml-auto">
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              )}
                            </button>
                          ))}
                        </nav>
                      </div>

                      {/* Menu Footer */}
                      <div className="border-t border-purple-500/30 p-6">
                        <div className="text-center text-purple-300 text-sm flex items-center justify-center gap-2">
                          <p>Track your progress</p>
                        </div>
                        <div className="text-center text-purple-300 text-xs mt-1 flex items-center justify-center gap-2">
                          <p>Achieve your goals</p>
                          <img src={BullseyeIcon} alt="Goal" className="w-4 h-4 rounded" />
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {ActiveComponent && <ActiveComponent user={user} />}
      </main>

      {/* Footer */}
      <footer className="bg-black/20 backdrop-blur-sm border-t border-purple-500/20 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-purple-300 flex items-center justify-center gap-2">
            <p className="text-sm">Track your progress, achieve your goals</p>
            <img src={BullseyeIcon} alt="Goal" className="w-4 h-4 rounded" />
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/" element={<MainApp />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;